import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';

import { AUDIT_REASON_MIN_LENGTH } from '@dayon/contracts';

import { api, ApiRequestError } from '../../lib/api.ts';
import type { AgentRow, PortalAccess, PortalInviteLink } from './types.ts';
import { PORTAL_STATUS_LABEL, portalTone, stamp } from './vocabulary.tsx';

/**
 * Give an agent a login to its own portal, or re-issue the link.
 *
 * ── THE DEFECT THIS CLOSES ─────────────────────────────────────────────────
 *
 * Creating an agent worked and recording its KYB pass worked, and then the
 * journey stopped: nothing in the API ever wrote an `identity.partner_user`
 * row, so every agent this console has ever created was permanently locked out
 * of the agent portal. The only agents who could sign in were the handful the
 * seed wrote, and the only remedy was an engineer running an INSERT.
 *
 * ── WHY THE LINK IS STILL SHOWN, NOW THAT IT IS ALSO SENT ──────────────────
 *
 * `@dayon/mailer` records the partner invite in the same transaction as the
 * token, so the two commit together or not at all, and a worker hands it to the
 * provider a minute later. What the platform CANNOT promise is delivery:
 * MAIL_OUTBOUND_MODE decides who may receive anything at all, and development
 * and staging run with it suppressed — every message recorded, none sent.
 *
 * So this dialog reports the status the server gave it for THAT message and
 * shows the link either way. A screen that claimed a send would be wrong on
 * every non-production environment, and one that hid the link because it
 * believed its own claim would leave the operator with nothing to hand over.
 *
 * The token is 32 random bytes and the database stores only its SHA-256 digest.
 * It is shown once here and never again: re-opening this dialog reads the
 * STATE, not the token, and a lost link is replaced rather than recovered.
 *
 * ── WHY THERE IS NO AUTHENTICATOR STEP ─────────────────────────────────────
 *
 * The operator invite promises "2FA must be set up before the first sign-in"
 * and enrols one, because operator sign-in demands a code. Agent sign-in does
 * not — `POST /api/agent/sign-in` checks the password and mints the session in
 * the same breath. Asking the agent to scan a QR code would protect nothing and
 * would tell them their account was behind a factor when it is not, so this
 * dialog does not, and says why.
 *
 * ── TWO WORDS THAT COLLIDE ─────────────────────────────────────────────────
 *
 * `agent.agent.status` and `identity.partner_user.status` both use INVITED and
 * they mean different things: the first is an agent whose KYB has not been
 * recorded, the second is a person holding a link they have not accepted. Every
 * sentence below names which one it is talking about, and the portal label says
 * "Can sign in" rather than "Active" so the two chips can never be read as one.
 */
export function PortalAccessDialog({
  agent,
  canWrite,
  onClose,
  onDone,
}: {
  agent: AgentRow | null;
  /** `partners.manage`. Reading the state needs only `partners.view`. */
  canWrite: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();

  /* Ending access is a two-press action: the first press asks for the reason,
     the second performs it. Not a confirm dialog on top of a dialog — the
     reason is the confirmation, and a required sentence is a better guard
     against a misclick than an "Are you sure?" nobody reads. */
  const [endingAccess, setEndingAccess] = useState(false);
  const [reason, setReason] = useState('');
  const { toast } = useToast();

  const [state, setState] = useState<PortalAccess | null>(null);
  const [loading, setLoading] = useState(false);
  /*
   * Did the read FAIL, or did it succeed? The error modal cannot carry that on
   * its own — it is dismissed in a second, and what stands behind it would
   * otherwise be the copy written for "this agent has no login", which is a
   * fact an operator would act on by provisioning a second one.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  /** The link, held only until this dialog closes. Shown once. */
  const [issued, setIssued] = useState<PortalInviteLink | null>(null);
  /*
   * WHICH LINK is on screen. An invite and a reset return the same shape and
   * mean opposite things — one welcomes somebody who has never signed in, the
   * other explains to somebody who has that they were just cut off — so the
   * copy beside the link cannot be shared, and this is what chooses it.
   */
  const [issuedKind, setIssuedKind] = useState<'INVITE' | 'RESET'>('INVITE');
  /* Resetting is a two-press action for the same reason ending access is: the
     required sentence IS the confirmation, and a sentence somebody has to write
     is a better guard against a misclick than an "Are you sure?" nobody reads. */
  const [resetting, setResetting] = useState(false);
  /*
   * RESTORING is a two-press action too, and it was a one-press action until item
   * 41 — "record the reason for a data alteration". The suspension carried its
   * reason and the restoration carried nothing, so the trail said why access was
   * taken away and never why it was given back. Those are the same question asked
   * twice and only one half was answerable.
   */
  const [restoring, setRestoring] = useState(false);
  /** The role a new login is provisioned at. The SERVER says which are legal. */
  const [portalRole, setPortalRole] = useState('OWNER');

  const agentId = agent?.id ?? null;

  const load = useCallback(() => {
    if (agentId === null) return;
    setLoading(true);
    api
      .get<PortalAccess>(`/api/admin/agents/${agentId}/portal-access`)
      .then((payload) => {
        setState(payload);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        // Emptied deliberately: a stale state under a failure notice reads as
        // this agent's answer.
        setState(null);
        setLoadFailed(true);
        showError({
          title: 'Could not read the portal access state',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The agent’s portal login could not be fetched. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setLoading(false));
  }, [agentId, showError]);

  useEffect(() => {
    if (agentId === null) return;
    load();
  }, [agentId, load]);

  /*
   * KEEP THE CHOSEN ROLE LEGAL.
   *
   * The dropdown defaults to OWNER because that is the first login a partner
   * gets. Once one exists the server stops offering it, and a form still
   * holding OWNER would post a role the handler refuses — an error dialog for
   * a choice the operator never made. Following the server's own list is also
   * what makes this screen correct the day the model changes.
   */
  useEffect(() => {
    const allowed = state?.provisionableRoles ?? [];
    if (allowed.length > 0 && !allowed.includes(portalRole)) setPortalRole(allowed[0]!);
  }, [state, portalRole]);

  const close = () => {
    setState(null);
    setLoadFailed(false);
    setEmail('');
    setDisplayName('');
    setFields({});
    setIssued(null);
    setIssuedKind('INVITE');
    setResetting(false);
    setPortalRole('OWNER');
    setEndingAccess(false);
    setRestoring(false);
    setReason('');
    onClose();
  };

  /** Both writes land here: they differ only in the route and the sentence. */
  const write = async (path: string, payload: Record<string, unknown> | undefined, done: string) => {
    setBusy(true);
    try {
      const result = await api.post<PortalInviteLink>(path, payload);
      setIssued(result);
      setResetting(false);
      setReason('');
      setFields({});
      toast(done);
      // The list carries portal_status, so the row behind this dialog is stale
      // the moment this succeeds.
      onDone();
      load();
    } catch (error: unknown) {
      // A field problem stays inline; a refusal is a decision that did NOT
      // happen, and that goes to the modal.
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not grant portal access',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Suspension and reinstatement, which return a decision rather than a link.
   *
   * Deliberately NOT `write()`: that one sets `issued`, which makes the dialog
   * hand over an invite link and take over the screen. Ending access issues
   * nothing, and routing it through `write` would have printed a stale link
   * beside the words "access ended".
   */
  const act = async (path: string, payload: Record<string, unknown> | undefined, done: string) => {
    setBusy(true);
    try {
      await api.post(path, payload);
      setFields({});
      setEndingAccess(false);
      setRestoring(false);
      setReason('');
      toast(done);
      onDone();
      load();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Nothing was changed',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The change did not go through. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const endAccess = () => {
    /* The floor is the DATABASE's, imported rather than written here. It said 8 in
       this file while `platform.audit_log` said 10, so a nine-character reason
       passed this check, reached the server, and came back as "That change is not
       allowed by the platform rules" — the operator told the platform forbids
       ending access, when they were one character short. */
    if (reason.trim().length < AUDIT_REASON_MIN_LENGTH) {
      setFields({
        reason:
          'Say why in a sentence — the audit trail keeps no other record of it. At least ' +
          `${AUDIT_REASON_MIN_LENGTH} characters.`,
      });
      return;
    }
    if (agentId === null) return;
    void act(
      `/api/admin/agents/${agentId}/portal-access/suspend`,
      { reason: reason.trim() },
      'Access ended. Any session they had open is already refused.',
    );
  };

  const reinstate = () => {
    if (reason.trim().length < AUDIT_REASON_MIN_LENGTH) {
      setFields({
        reason:
          'Say why access is being restored — the suspension says why it was taken away, ' +
          `and this is the only record of why it came back. At least ${AUDIT_REASON_MIN_LENGTH} characters.`,
      });
      return;
    }
    if (agentId === null) return;
    void act(
      `/api/admin/agents/${agentId}/portal-access/reinstate`,
      { reason: reason.trim() },
      'Access restored. The sessions they had open when it was pulled stay closed.',
    );
  };

  const provision = () => {
    const problems: Record<string, string> = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      problems['email'] = 'Enter a valid email address for the partner.';
    }
    if (displayName.trim().length < 2) {
      problems['displayName'] = 'Give the person’s name as it should appear in their portal.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    if (agentId === null) return;
    void write(
      `/api/admin/agents/${agentId}/portal-access`,
      { email: email.trim(), displayName: displayName.trim(), portalRole },
      `${email.trim()} can accept the invite once you send them the link.`,
    );
  };

  /**
   * RESET THE CREDENTIAL, which is not re-issuing the invite.
   *
   * A separate route, a separate audit action and a separate table — and it
   * does the thing re-issuing deliberately never does: it ends every session
   * this login is holding and stops the password they are using, in the same
   * statement. The screen says so before the press and the server says so
   * after, because an operator who believes they are only sending an email is
   * making a decision they do not know they are making.
   */
  const resetPassword = () => {
    if (reason.trim().length < AUDIT_REASON_MIN_LENGTH) {
      setFields({
        reason:
          'Say why in a sentence — this ends their sessions, and the audit trail keeps no ' +
          `other record of the reason. At least ${AUDIT_REASON_MIN_LENGTH} characters.`,
      });
      return;
    }
    if (agentId === null) return;
    setIssuedKind('RESET');
    void write(
      `/api/admin/agents/${agentId}/portal-access/reset-password`,
      { reason: reason.trim() },
      'Password reset. They are signed out everywhere and their old password no longer works.',
    );
  };

  const reissue = () => {
    if (agentId === null) return;
    setIssuedKind('INVITE');
    void write(
      `/api/admin/agents/${agentId}/portal-access/invite-link`,
      undefined,
      'A fresh link was issued. Every earlier one is now dead.',
    );
  };

  const copyLink = () => {
    if (issued === null) return;
    navigator.clipboard
      .writeText(issued.linkUrl)
      .then(() => toast('Invite link copied — give it to them yourself.'))
      .catch(() =>
        showError({
          title: 'Could not copy the invite link',
          message:
            'The browser refused clipboard access. Select the link on screen and copy it by ' +
            'hand — it is shown only once.',
        }),
      );
  };

  const access = state?.access ?? null;
  const portalStatus = access?.status ?? 'NONE';

  return (
    <Dialog
      open={agent !== null}
      onClose={close}
      wide
      title={agent === null ? '' : `Portal access · ${agent.ref}`}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {issued === null ? 'Cancel' : 'Done'}
          </Button>
          {issued === null && canWrite && !endingAccess && !resetting && !restoring &&
            state?.canProvision === true && (
              <Button variant="primary" busy={busy} onClick={provision}>
                {/* The label follows what the press would ACTUALLY create,
                    which the server decides: the owner while there is none,
                    a staff login once there is. A button that said "Grant
                    portal access" over a partner that already has one would
                    read as a second owner. */}
                {state.provisionableRoles[0] === 'OWNER'
                  ? 'Grant portal access'
                  : 'Add a staff login'}
              </Button>
            )}
          {issued === null && canWrite && !endingAccess && !resetting && !restoring &&
            access?.status === 'INVITED' && (
              <Button variant="primary" busy={busy} onClick={reissue}>
                Re-issue invite link
              </Button>
            )}
          {/* RESET, offered only on a login that has actually accepted. There
              is nothing to reset on an invite that was never used, and the
              server refuses it in those words. */}
          {issued === null && canWrite && !endingAccess && !resetting && !restoring &&
            access?.status === 'ACTIVE' && (
              <Button variant="ghost" busy={busy} onClick={() => setResetting(true)}>
                Reset password
              </Button>
            )}
          {issued === null && canWrite && resetting && (
            <Button variant="primary" busy={busy} onClick={resetPassword}>
              Reset password and sign them out
            </Button>
          )}
          {issued === null && canWrite && !endingAccess && !resetting && !restoring &&
            (access?.status === 'INVITED' || access?.status === 'ACTIVE') && (
              <Button variant="ghost" busy={busy} onClick={() => setEndingAccess(true)}>
                End access
              </Button>
            )}
          {issued === null && canWrite && endingAccess && (
            <Button variant="primary" busy={busy} onClick={endAccess}>
              End access
            </Button>
          )}
          {/* RESTORE, in two presses like ending access and resetting — the first
              asks for the reason, the second performs it. The required sentence IS
              the confirmation; an "Are you sure?" nobody reads is not one, and the
              sentence is the only record of why access came back. */}
          {issued === null &&
            canWrite &&
            !resetting &&
            !endingAccess &&
            !restoring &&
            access?.status === 'SUSPENDED' && (
              <Button variant="primary" busy={busy} onClick={() => setRestoring(true)}>
                Restore access
              </Button>
            )}
          {issued === null && canWrite && restoring && (
            <Button variant="primary" busy={busy} onClick={reinstate}>
              Restore access
            </Button>
          )}
        </>
      }
    >
      {agent && (
        <div className="stack">
          {/* ── The link, when one has just been issued ────────────────────
              It takes over the dialog: it is the one thing the operator has to
              take away, and leaving the form beside it invites a second press
              that would revoke the link they are holding. */}
          {issued !== null ? (
            <>
              {/* WHICH ACT just happened. An invite and a reset return the same
                  shape and are opposite events: one is a welcome, the other is
                  a lockout with a way back. Printing the invite's sentence over
                  a reset would hide the thing the operator most needs to know
                  — that the partner is already signed out. */}
              {issuedKind === 'RESET' ? (
                <p style={{ margin: 0 }}>
                  <strong>{issued.displayName}</strong> ({issued.email}) is{' '}
                  <strong>signed out of every device</strong> and the password they were using no
                  longer works. The link below is the only way back into {agent.name}
                  &rsquo;s portal, and it expires {stamp(issued.expiresAt)}.
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  <strong>{issued.displayName}</strong> ({issued.email}) can now set a password
                  for {agent.name}&rsquo;s portal. The invite expires {stamp(issued.expiresAt)}.
                  {issued.supersededLinks > 0 && (
                    <>
                      {' '}
                      {issued.supersededLinks === 1
                        ? 'The earlier link is now dead.'
                        : `The ${issued.supersededLinks} earlier links are now dead.`}
                    </>
                  )}
                </p>
              )}
              {issued.delivery.status === 'PENDING' ? (
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    <strong>
                      Queued for {issued.delivery.to} as {issued.delivery.messageRef}.
                    </strong>{' '}
                    The platform sends it within the minute. Nothing here can confirm it ARRIVED,
                    so copy the link anyway — it is shown <strong>once</strong>, the platform keeps
                    only a one-way fingerprint of it, and a lost link can only be replaced, never
                    recovered.
                  </div>
                </div>
              ) : (
                <div className="notice">
                  <Icon name="warning-circle" />
                  <div>
                    <strong>
                      Recorded as {issued.delivery.messageRef}, and NOT sent.
                    </strong>{' '}
                    {/* SUPPRESSED: mail off, an address outside the allow-list, or a suppressed
                        address — "mail is switched off" was false for the allow-list case. */}
                    This installation does not send mail to {issued.delivery.to}, or the address is
                    on the do-not-mail list — either way the message is marked as held back and
                    nobody will receive it. Deliver this link yourself, and copy it now: it is shown{' '}
                    <strong>once</strong>, the platform keeps only a one-way fingerprint of it, and a
                    lost link can only be replaced, never recovered.
                  </div>
                </div>
              )}

              <div className="notice notice-quiet">
                <Icon name="link-simple" />
                <div>
                  {/* Wrapped so a 43-character token cannot push the dialog
                      into a horizontal scroll on a phone. */}
                  <code style={{ overflowWrap: 'anywhere' }}>{issued.linkUrl}</code>
                </div>
              </div>

              <div className="row">
                <Button onClick={copyLink}>
                  <Icon name="copy" />
                  Copy invite link
                </Button>
              </div>
            </>
          ) : loading ? (
            <div className="skeleton" style={{ height: 'var(--space-9)' }}>
              &nbsp;
            </div>
          ) : loadFailed ? (
            <div className="notice">
              <Icon name="warning-circle" />
              <div>
                {agent.ref}&rsquo;s portal access could not be fetched, so this dialog knows{' '}
                <strong>nothing</strong> about their login — not that they have none. Close this and try again rather than granting one, in case there is
                already an account on the address you were about to type.
              </div>
            </div>
          ) : (
            <>
              {/* ── What state it is in ────────────────────────────────── */}
              <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                <Tag tone={portalTone(portalStatus)}>
                  {PORTAL_STATUS_LABEL[portalStatus] ?? portalStatus}
                </Tag>
                {access !== null && <span className="text-subtle">{access.email}</span>}
              </div>

              {access === null ? (
                <p style={{ margin: 0 }}>
                  {agent.name} has <strong>no portal login at all</strong>. Nobody on their side
                  can sign in to see a downline, a statement or a payout — an agent created through
                  this console starts with no account, and this is where one is made.
                </p>
              ) : access.status === 'INVITED' ? (
                <p style={{ margin: 0 }}>
                  <strong>{access.displayName}</strong> was invited and has{' '}
                  <strong>not accepted yet</strong>, so they still cannot sign in: the account has
                  no password, and the sign-in screen treats it exactly as it treats an address it
                  has never seen.
                  {access.invite === null
                    ? ' There is no live link — every one issued has been used or replaced. Re-issue to give them a new one.'
                    : access.invite.expired
                      ? ` Their link expired on ${stamp(access.invite.expiresAt)}. Re-issue to give them a fresh one.`
                      : ` Their link is live until ${stamp(access.invite.expiresAt)}.`}
                </p>
              ) : access.status === 'SUSPENDED' ? (
                <p style={{ margin: 0 }}>
                  <strong>{access.displayName}</strong> is <strong>suspended</strong> and cannot
                  sign in. Any session they were holding when it was ended is already refused, and
                  any invite link they had was revoked with it. Restoring access lets them sign in
                  again; it does not reopen the sessions that were closed.
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  <strong>{access.displayName}</strong> has accepted and can sign in.
                  {access.lastSignInAt === null
                    ? ' They have not signed in yet.'
                    : ` Last signed in ${stamp(access.lastSignInAt)}.`}
                </p>
              )}

              {/* ── THE OFFICE, not only the owner ─────────────────────────
                  `identity.partner_user.portal_role` has carried four values
                  since the schema was written and this console only ever
                  provisioned OWNER, so this list had exactly one row for every
                  partner on the platform. It can have several now, and each
                  reaches only what its role allows.

                  Every row states three separate facts, because no two of them
                  mean the same thing: whether a password was ever set, whether
                  an ACCEPTANCE link is outstanding, and whether a RESET link
                  is. A row with a live reset link is a partner whose account is
                  ACTIVE and who cannot sign in — the one state this panel could
                  not describe at all before. */}
              {(state?.staff.length ?? 0) > 1 && (
                <div className="notice notice-quiet">
                  <Icon name="users-three" />
                  <div>
                    <strong>{state!.staff.length} logins under this agent.</strong>
                    <ul style={{ margin: 'var(--space-2) 0 0', paddingInlineStart: 'var(--space-4)' }}>
                      {state!.staff.map((member) => (
                        <li key={member.id}>
                          {member.displayName} · {member.email} ·{' '}
                          {member.portalRole.charAt(0) + member.portalRole.slice(1).toLowerCase()} ·{' '}
                          {member.resetExpiresAt !== null
                            ? `password reset pending, link expires ${stamp(member.resetExpiresAt)}`
                            : member.status === 'SUSPENDED'
                              ? 'suspended'
                              : !member.hasPassword
                                ? member.inviteExpiresAt === null
                                  ? 'invited, no live link'
                                  : `invited, link expires ${stamp(member.inviteExpiresAt)}`
                                : member.lastSignInAt === null
                                  ? 'can sign in, never has'
                                  : `last signed in ${stamp(member.lastSignInAt)}`}
                        </li>
                      ))}
                    </ul>
                    Ending, resetting and re-issuing above act on the{' '}
                    <strong>owner&rsquo;s</strong> login, and only that one. Staff logins are
                    added and removed by the owner in the agent&rsquo;s own portal — this panel
                    lists them so you can see who holds one, but offers no control that picks a
                    staff row, and saying so is better than a button that quietly hit the wrong
                    person.
                  </div>
                </div>
              )}

              {/* ── Why access is being ended ──────────────────────────────
                  Required, and stored on the audit row: `identity.partner_user`
                  records that access ended and when, and nothing else records
                  why. Six months later that is the difference between a
                  contract that lapsed and a partner under investigation. */}
              {endingAccess && canWrite && (
                <Field
                  label="Why is this login being ended?"
                  error={fields['reason']}
                  hint="Goes on the audit record. The partner is not told: suspension sends no message, because there is no template for one and a notice drafted from a free-text reason is not a decision to make in a hint. Tell them yourself."
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      autoComplete="off"
                      value={reason}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      placeholder="Agreement ended 30 September."
                      onChange={(event) => setReason(event.target.value)}
                    />
                  )}
                </Field>
              )}

              {/* ── Why access is being restored ───────────────────────────
                  The other half of the suspension, and the half that used to go
                  unrecorded. `identity.partner_user` records that access came back
                  and when; this is the only thing that records why — which is what
                  separates "the contract was renewed" from "we were wrong". */}
              {restoring && canWrite && (
                <Field
                  label="Why is this login being restored?"
                  error={fields['reason']}
                  hint="Goes on the audit record beside the reason it was ended, so the two can be read as one decision. The partner is not told: restoring sends no message, and a notice drafted from a free-text reason is not a decision to make in a hint."
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      autoComplete="off"
                      value={reason}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      placeholder="Agreement renewed from 1 November."
                      onChange={(event) => setReason(event.target.value)}
                    />
                  )}
                </Field>
              )}

              {/* ── Why the credential is being reset ──────────────────────
                  A DIFFERENT act from ending access, and the warning above the
                  box is the point of the whole control: the operator is about
                  to sign this partner out of every device and stop the password
                  they are using right now. The old "re-issue invite link"
                  button did none of that and could not have said so. */}
              {resetting && canWrite && (
                <>
                  <div className="notice">
                    <Icon name="warning-circle" />
                    <div>
                      <strong>This cuts them off immediately.</strong> Every session{' '}
                      {access?.displayName ?? 'this login'} has open is refused on their next
                      click, and the password they are using stops working at the same instant —
                      not when they next sign in. They can only get back in with the link this
                      creates, which is emailed to {access?.email ?? 'their address'} and shown
                      to you once.
                    </div>
                  </div>
                  <Field
                    label="Why is this password being reset?"
                    error={fields['reason']}
                    hint="Goes on the audit record and nowhere else — the message they receive says a reset happened and who to ask, never your reason for it."
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        autoComplete="off"
                        value={reason}
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        placeholder="Owner reported the password was shared with a contractor."
                        onChange={(event) => setReason(event.target.value)}
                      />
                    )}
                  </Field>
                </>
              )}

              {/* ── The form, only when provisioning is what happens next ── */}
              {state?.canProvision === true && canWrite && !endingAccess && !resetting && !restoring && (
                <>
                  <Field
                    label="Email"
                    error={fields['email']}
                    hint="This is the address they sign in with. One address is one portal login, across both partner portals."
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        type="email"
                        autoComplete="off"
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setFields({});
                        }}
                        placeholder="owner@luzonbets.ph"
                      />
                    )}
                  </Field>

                  <Field
                    label="Their name"
                    error={fields['displayName']}
                    hint="Shown to them in their own portal, and recorded in the audit trail beside yours."
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        value={displayName}
                        onChange={(e) => {
                          setDisplayName(e.target.value);
                          setFields({});
                        }}
                        placeholder="Rico Bautista"
                      />
                    )}
                  </Field>


                  {/* ── Which role ────────────────────────────────────────
                      The options come from the SERVER, not from a list here:
                      OWNER while the partner has none, the three staff roles
                      once it has one. A dropdown offering OWNER over a partner
                      that already has an owner would be a control that always
                      fails, and `partner_user_one_owner_idx` would refuse it in
                      the database whatever this screen offered. */}
                  {(state?.provisionableRoles ?? []).length > 1 && (
                    <Field
                      label="Role"
                      hint="What this login may reach. It cannot be changed in place afterwards — a role change is a new login and the old one ended, so the audit trail says who held what and for how long."
                    >
                      {({ id, describedBy }) => (
                        <Select
                          id={id}
                          aria-describedby={describedBy}
                          value={portalRole}
                          onChange={(e) => setPortalRole(e.target.value)}
                        >
                          {(state?.provisionableRoles ?? []).map((r) => (
                            <option key={r} value={r}>
                              {r.charAt(0) + r.slice(1).toLowerCase()}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  )}
                  <div className="notice notice-quiet">
                    <Icon name="info" />
                    <div>
                      {portalRole === 'OWNER' ? (
                        <>
                          This creates the <strong>owner</strong> login — &ldquo;only you can
                          change payout details&rdquo;, and an agent has exactly one. Staff
                          accounts come after it, at manager, bookkeeper or recruiter, and each
                          reaches only what its role allows.
                        </>
                      ) : (
                        <>
                          This creates a <strong>staff</strong> login beside the owner&rsquo;s.
                          What it reaches is decided by the role and not by this form: a{' '}
                          <strong>manager</strong> sees earnings, statements, the downline and the
                          office list but cannot change payout details; a{' '}
                          <strong>bookkeeper</strong> sees earnings, statements and payouts and
                          nothing else; a <strong>recruiter</strong> gets tracking links and
                          creatives but not the player list, because every row of that list
                          carries deposits and GGR.
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ── Why not, when it cannot happen ─────────────────────── */}
              {state !== null && state.refusal !== null && (
                <div className="notice">
                  <Icon name="warning-circle" />
                  <div>{state.refusal}</div>
                </div>
              )}

              {!canWrite && (
                <div className="notice notice-quiet">
                  <Icon name="lock-key" />
                  <div>
                    Granting a partner a credential needs <code>partners.manage</code>, which your
                    role does not hold. What is above is still yours to read.
                  </div>
                </div>
              )}

              <div className="notice notice-quiet">
                <Icon name="info" />
                <div>
                  <strong>No authenticator app is enrolled, and that is deliberate.</strong> The
                  agent portal&rsquo;s sign-in verifies a password and nothing else — it never asks
                  for a code — so enrolling a second factor here would protect nothing and would
                  tell the partner their account was behind one. An operator invite is different
                  and does enrol one, because the operator sign-in genuinely asks.
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
